import React, { ReactNode } from 'react';
import { Clipboard, LayoutAnimation } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBReactionButtonWithPicker } from '@shared/scenes/chat/thread/components/reaction/buttonWithPicker';
import { MBInvisibleReactionPicker } from '@shared/scenes/chat/thread/containers/messageInvisibleReactionPicker';
import { MBMessageReactionsAndSeenBy } from '@shared/scenes/chat/thread/containers/messageReactionsAndSeenBy';
import { MBActionSheet } from '@shared/util/actionSheet';
import { MBAlert } from '@shared/util/alert';
import { MBAnalytics } from '@shared/util/analytics';
import { useBoolean } from '@shared/util/hooks';
import { MBLinking } from '@shared/util/linking';
import { MBMessageDeliveryStatus } from '../deliveryStatus';
import { MBUrgentBadge } from '../urgentBadge';
import { MBMessageBubbleAttachments } from './components/attachments';
import { MBMessageBubbleContent } from './components/content';
import { MBSenderImage } from './components/senderImage';
import { MBSenderName } from './components/senderName';
import { MBSentBySMS } from './components/sentBySMS';
import { Styles, UnvalidatedStyles } from './styles';

export interface MBMessageBubbleProps {
  message: MBAPI.ChatMessage;
  layout: MBChatMessageLayoutOptions;
  theme: MBMessageBubbleTheme;
  deliveryStatus?: MBDeliveryStatus;

  /*
        Used by reaction buttons etc to determine if should call mutation
        for channel message or thread comment.
        TODO(fant): remove this when chat messages have been unified.
    */
  channelSlug: string;

  /*
        Callback for when message bubble is pressed. If this is not set,
        the default press action will be showing message details (aka time sent)
    */
  onPress?: () => void;

  /*
        Callback for when the user wants to remove the message. When this
        prop is sent "Remove Message" will be shown on long press. So this
        property should not be set when the user cannot remove (eg for
        messages sent by other users).
    */
  onDelete?: () => void;

  /*
        Callback for when the user wants to edit the message
    */
  onEdit?: () => void;

  /*
        TODO(fant): when searching and selecting a message, we need to scroll
        to it in a chat thread. This does not work well together with link
        previews since they're loaded async and push around the entire content.
        For now, we solve this by simply hiding link previews when showing
        search results.
    */
  isHidingLinkPreview?: boolean;

  isDesktop: boolean;
  marginLeft?: number;
}

interface MBMessageBubbleState {
  isShowingDetails: boolean;
}

interface RenderWrapperProps extends MBMessageBubbleProps {
  messageBubble: MBMessageBubble;
}

/**
 * Light wrapper for MBMessageBubble below.
 *
 * Provides sane MBHover functionality.
 */
function RenderWrapper({
  message,
  channelSlug,
  layout,
  theme,
  deliveryStatus,
  isDesktop,
  isHidingLinkPreview,
  messageBubble,
}: RenderWrapperProps) {
  const [isHovered, onHoverStart, onHoverEnd] = useBoolean(!isDesktop); // treat hovered as true on mobile

  return (
    <PressableOnMobileAndSelectableOnDesktop isDesktop={isDesktop} messageBubble={messageBubble}>
      <>
        {/*
            This is somewhat of a hack that needs explaining... To easily show the
            reaction picker when pressing "Add Reaction..." on message long press,
            we want to reuse our picker logic from reaction/buttonWithPicker to
            not have to copy code. To easily do that, we have this thin component
            that is basically an invisible MBReactionButtonWithPicker. When we
            want to add a reaction from an MBActionSheet, we simply get the ref
            of this component and call showPicker. Somewhat hacky, but fairly clean.
          */}
        <MBInvisibleReactionPicker
          ref={ref => {
            messageBubble.reactionPicker = ref;
          }}
          message={message}
          channelSlug={channelSlug}
        />

        <View>
          {messageBubble.isShowingSenderName && (
            <MBSenderName
              name={message.sender.isMe ? undefined : message.sender.fullName}
              align={theme.align}
              imagePlaceholder={theme.showImage}
              date={message.createdAt}
            />
          )}

          {/* render the content containing bubble and attachment */}
          <MBView
            style={[
              theme.align === 'left' && Styles.rowAlignLeft,
              theme.align === 'right' && Styles.rowAlignRight,
            ]}
          >
            {theme.showImage && <MBSenderImage message={message} layout={layout} />}

            <View fill>
              {/* 1. at the very top, render the urgent badge */}
              {!!message.urgent && <MBUrgentBadge align={theme.align} />}

              {/* 2. then render the message bubble and its content */}
              <MBHover {...{ onHoverStart, onHoverEnd }}>
                <MBMessageBubbleContent
                  onLongPressLinkPreview={messageBubble.onLongPressLinkPreview}
                  theme={theme}
                  message={message}
                  layout={layout}
                  channelSlug={channelSlug}
                  isHidingLinkPreview={isHidingLinkPreview}
                  hovering={isHovered}
                  isFirstMessageInGroup={messageBubble.isShowingSenderName}
                />
              </MBHover>

              {/* 3. all attachments in a long list */}
              <MBMessageBubbleAttachments
                message={message}
                align={theme.align}
                channelSlug={channelSlug}
                isSent={messageBubble.isSent}
                onLongPress={messageBubble.onLongPress}
                blink={!!theme.blinkBackgroundColor}
                isFirstMessageInGroup={messageBubble.isShowingSenderName}
              />
            </View>
          </MBView>

          {/*
                            Note: the following elements are rendered below the actual message content.
                            Otherwise the sender image would be pushed up to not be bottom aligned with
                            the message bubble.
                        */}
          <View row>
            {/*
                                Add a placeholder for the image so that left aligned content
                                (eg sent by) is aligned with the message bubble
                            */}
            {theme.showImage && <MBView style={Styles.userImagePlaceholder} />}

            <MBView style={[Styles.flex, theme.align === 'right' && Styles.alignRight]}>
              {/* 4. if the message has been sent by SMS or as urgent, show that */}
              {messageBubble.isSent && <MBSentBySMS message={message} />}

              {/*
                                    5. a row of reactions and the seen by indicator
                                    Note: hiding seen by in grouped messages will
                                    make the user miss some seen bys
                                */}
              <MBMessageReactionsAndSeenBy
                message={message}
                layout={layout}
                align={theme.align}
                isDM={theme.isDM}
                pickerBottomMargin={UnvalidatedStyles.reactionPickerBottomMargin}
                channelSlug={channelSlug}
              />
            </MBView>
          </View>

          {/*
                            Lastly, if we have a delivery status (only relevant for
                            the blue me bubble), render that status.
                            Note: don't show this if the message is already seen by someone
                            Note: hiding seen by in grouped messages will make
                                the user miss some seen bys
                        */}
          {!!deliveryStatus && !layout.seenByUsers.length && (
            <MBMessageDeliveryStatus
              status={deliveryStatus}
              isEdited={!!message.editedAt}
              style={Styles.deliveryStatus}
              testID="chat-message-delivered"
            />
          )}
        </View>
      </>
    </PressableOnMobileAndSelectableOnDesktop>
  );
}

function PressableOnMobileAndSelectableOnDesktop({
  children,
  isDesktop,
  messageBubble,
}: {
  children: ReactNode;
  isDesktop: boolean;
  messageBubble: MBMessageBubble;
}) {
  if (isDesktop) {
    return <>{children}</>;
  } else {
    return (
      <MBTouchableOpacity
        activeOpacity={0.7}
        onPress={messageBubble.onPress}
        onLongPress={messageBubble.onLongPress}
      >
        {children}
      </MBTouchableOpacity>
    );
  }
}

/*
    Message bubble base. Aligns and colors the message bubble depending on theme.
    TODO(fant): The complexity of this is already pretty big. How can we make it
    easier to disentangle?

    This contains:
      * urgent badge
      * message content
      * attachments
      * sent by sms text
      * reactions and seen by

    Note: this component doesn't show delivery status (compared to me.tsx) because
    all messages from other people will (by definition) already be delivered.
*/
export class MBMessageBubble extends React.PureComponent<
  MBMessageBubbleProps,
  MBMessageBubbleState
> {
  state: MBMessageBubbleState = { isShowingDetails: false };
  reactionPicker: MBReactionButtonWithPicker | null = null;

  toggleDetails = () => {
    const willShowDetails = !this.state.isShowingDetails;
    this.setState({ isShowingDetails: willShowDetails });
    if (!!LayoutAnimation) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }

    if (willShowDetails) {
      MBAnalytics.track('Message::ShowDetails', { messageId: this.props.message.id });
    }
  };

  get isSent(): boolean {
    return !this.props.deliveryStatus || this.props.deliveryStatus === 'sent';
  }

  get isShowingSenderName(): boolean {
    if (this.state.isShowingDetails) {
      return true;
    }
    if (this.props.layout.isGroupedWithAbove) {
      return false;
    }
    return this.isSent;
  }

  confirmDelete = () =>
    MBAlert.confirm({
      title: 'Delete message',
      message: 'Are you sure you want to delete this message? This action cannot be undone.',
      confirmText: 'Yes, delete',
      confirmStyle: 'destructive',
      onConfirm: this.props.onDelete,
    });

  onPress = () => {
    if (!!this.props.onPress) {
      this.props.onPress();
    } else {
      this.toggleDetails();
    }
  };

  onLongPress = () =>
    MBActionSheet.show({
      parent: this,
      webHorizontalAlignment: this.props.theme.align,
      webVerticalAlignment: 'above',
      options: [
        {
          title: 'Add a Reaction...',
          hidden: !this.isSent,
          onPress: () => this.reactionPicker?.showPicker(),
        },
        {
          title: 'Copy Text',
          hidden: !this.props.message.content,
          onPress: () => {
            Clipboard.setString(this.props.message.content);
            MBAlert.alert({
              title: 'Copied to clipboard',
              confirmText: 'OK',
            });
          },
        },
        {
          title: 'Edit Message',
          hidden: !this.props.message.sender.isMe || !this.props.message.content.length,
          onPress: this.props.onEdit,
        },
        {
          title: 'Delete Message',
          hidden: !this.props.onDelete || !this.props.message.sender.isMe,
          destructive: true,
          onPress: this.confirmDelete,
        },
      ],
    });

  onLongPressLinkPreview = (url: string) =>
    MBActionSheet.show({
      parent: this,
      webHorizontalAlignment: 'center',
      webVerticalAlignment: 'above',
      options: [
        {
          title: 'Visit Link',
          onPress: () => MBLinking.open(url),
        },
        {
          title: 'Add a Reaction...',
          hidden: !this.isSent,
          onPress: () => this.reactionPicker?.showPicker(),
        },
        {
          title: 'Edit Message',
          hidden: !this.props.onEdit,
          onPress: () => this.props.onEdit?.(),
        },
        {
          title: 'Delete Message',
          hidden: !this.props.onDelete,
          destructive: true,
          onPress: this.confirmDelete,
        },
      ],
    });

  render() {
    return <RenderWrapper {...this.props} messageBubble={this} />;
  }
}
